import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import TableLoadingRow from "./shared/TableLoadingRow";
import {
  inactivateRegistration,
  listActiveStudents,
  listFrequencies,
  printComprovativo,
  printFicha,
  removeStudent,
} from "../services/student";
import { levelKey } from "../utils/classesLabels";
import { listClassrooms } from "../services/classroom";
import { getUserInfo, hasSpecialHourSchedule } from "../services/auth";
import NotifyModal from "./NotifyModal";
import StudentExtraModal from "./StudentExtraModal";
import AllocateClassroomModal from "./AllocateClassroomModal";
import { useConfirm } from "../hook/useConfirm";

const PAGE_SIZE = 8;
const currentYear = new Date().getFullYear();

// Color-coded tone per action, used both for the action's icon badge and its
// row hover state in the actions dropdown.
const ACTION_TONES = {
  primary: { badgeBg: "bg-primary-50", badgeText: "text-primary-600", hoverBg: "bg-hover-primary-50" },
  warning: { badgeBg: "bg-warning-focus", badgeText: "text-warning-main", hoverBg: "bg-hover-warning-focus" },
  danger: { badgeBg: "bg-danger-focus", badgeText: "text-danger-main", hoverBg: "bg-hover-danger-focus" },
  info: { badgeBg: "bg-info-focus", badgeText: "text-info-main", hoverBg: "bg-hover-info-focus" },
  success: { badgeBg: "bg-success-focus", badgeText: "text-success-main", hoverBg: "bg-hover-success-focus" },
  lilac: { badgeBg: "bg-lilac-50", badgeText: "text-lilac-600", hoverBg: "bg-hover-lilac-50" },
};

const ActionMenuItem = ({ icon, tone = "primary", label, onClick, modalTarget }) => {
  const { badgeBg, badgeText, hoverBg } = ACTION_TONES[tone];
  const modalProps = modalTarget
    ? { "data-bs-toggle": "modal", "data-bs-target": modalTarget }
    : undefined;
  return (
    <li>
      <button
        type='button'
        className={`w-100 d-flex align-items-center gap-10 px-10 py-8 radius-8 border-0 bg-transparent text-secondary-light text-start ${hoverBg}`}
        style={{ transition: "background-color .12s ease" }}
        onClick={onClick}
        {...modalProps}
      >
        <span
          className={`w-32-px h-32-px radius-8 d-flex align-items-center justify-content-center flex-shrink-0 ${badgeBg} ${badgeText}`}
        >
          <Icon icon={icon} className='text-md' />
        </span>
        <span className='text-sm fw-medium'>{label}</span>
      </button>
    </li>
  );
};
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

const ListaEstudantesLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();

  const [registrations, setRegistrations] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [name, setName] = useState("");
  const [sex, setSex] = useState("");
  const [turma, setTurma] = useState("");
  const [classroomId, setClassroomId] = useState("");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [notifyTargets, setNotifyTargets] = useState([]);
  const [extraModalMode, setExtraModalMode] = useState("add");
  const [extraModalTarget, setExtraModalTarget] = useState(null);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  // The actions menu is rendered through a portal into document.body and
  // positioned with `position: fixed`, instead of relying on Bootstrap's
  // in-flow dropdown — the table sits inside a `.table-responsive` scroll
  // container (overflow: auto), which clips an absolutely-positioned
  // dropdown-menu that extends past its bounds.
  const [openMenu, setOpenMenu] = useState(null); // { id, top, left, record }
  const menuRef = useRef(null);

  const fetchRegistrations = (year) => {
    setLoading(true);
    listActiveStudents(year)
      .then((resp) => setRegistrations(resp.data || []))
      .catch(() => setRegistrations([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRegistrations(filterYear);
    setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  useEffect(() => {
    listFrequencies()
      .then((resp) => setFrequencies(resp.data || []))
      .catch(() => setFrequencies([]));
  }, []);

  const fetchClassrooms = (year) => {
    listClassrooms(year)
      .then((resp) => setClassrooms(resp.data || []))
      .catch(() => setClassrooms([]));
  };

  // Turmas (classroom entity) are year-scoped, distinct from "Nível" (the
  // frequencies/level above) — refetched whenever the year filter changes.
  useEffect(() => {
    fetchClassrooms(filterYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  const frequencyFor = (record) =>
    frequencies.find(
      (frq) => String(frq.id) === String(record?.corseId) || frq.level === record?.student?.level,
    );

  // Maps studentId -> classroom, built from each classroom's allocated
  // student roster (there's no classroomId on the registration/student
  // record itself, only the reverse link via classroom.students).
  const studentClassroomMap = useMemo(() => {
    const map = new Map();
    classrooms.forEach((c) => {
      (c.students || []).forEach((s) => {
        map.set(String(s.id), c);
      });
    });
    return map;
  }, [classrooms]);

  const filtered = useMemo(() => {
    const term = name.trim().toLowerCase();
    return registrations.filter((r) => {
      const matchesName = !term || r.student?.name?.toLowerCase().includes(term);
      const matchesSex = !sex || r.student?.sex === sex;
      const matchesTurma =
        !turma ||
        String(r.corseId) === String(turma) ||
        String(r.student?.level) === String(turma);
      const matchesClassroom =
        !classroomId ||
        String(studentClassroomMap.get(String(r.studentId))?.id) === String(classroomId);
      return matchesName && matchesSex && matchesTurma && matchesClassroom;
    });
  }, [registrations, name, sex, turma, classroomId, studentClassroomMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const clearFilters = () => {
    setName("");
    setSex("");
    setTurma("");
    setClassroomId("");
    setPage(1);
    setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((cur) => {
      const allSelected = pageItems.length > 0 && pageItems.every((r) => cur.includes(r.id));
      if (allSelected) {
        const pageIds = new Set(pageItems.map((r) => r.id));
        return cur.filter((id) => !pageIds.has(id));
      }
      const merged = new Set(cur);
      pageItems.forEach((r) => merged.add(r.id));
      return Array.from(merged);
    });
  };

  const maleCount = registrations.filter((r) => r.student?.sex === "M").length;
  const femaleCount = registrations.filter((r) => r.student?.sex === "F").length;

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const MENU_WIDTH = 260;
  const MENU_GAP = 8;

  const toggleMenu = (e, record) => {
    e.stopPropagation();
    if (openMenu?.id === record.id) {
      setOpenMenu(null);
      return;
    }
    // Read currentTarget synchronously — React nulls it out once the event
    // finishes dispatching, and in StrictMode the setOpenMenu updater above
    // used to be re-invoked later (to detect impure reducers), by which
    // point `e.currentTarget` was already null.
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    // Position is only a first guess here (always below the trigger) — the
    // layout effect below measures the menu's real height once it mounts and
    // flips it upward when the row is near the bottom of the viewport.
    setOpenMenu({
      id: record.id,
      record,
      left,
      triggerTop: rect.top,
      triggerBottom: rect.bottom,
    });
  };

  const closeMenu = () => setOpenMenu(null);

  // Flip the actions menu above its trigger whenever there isn't enough
  // room below (e.g. the row sits near the bottom of the viewport), instead
  // of letting it render off-screen/clipped. Runs before paint so there's no
  // visible jump, then a rAF fades the menu in once it's correctly placed.
  useLayoutEffect(() => {
    if (!openMenu || !menuRef.current) return undefined;
    const menuEl = menuRef.current;
    const menuHeight = menuEl.getBoundingClientRect().height;
    const spaceBelow = window.innerHeight - openMenu.triggerBottom;
    const spaceAbove = openMenu.triggerTop;
    const openUpward = spaceBelow < menuHeight + MENU_GAP && spaceAbove > spaceBelow;

    const top = openUpward
      ? Math.max(MENU_GAP, openMenu.triggerTop - menuHeight - MENU_GAP)
      : Math.min(openMenu.triggerBottom + MENU_GAP, window.innerHeight - menuHeight - MENU_GAP);

    menuEl.style.top = `${top}px`;
    menuEl.style.transformOrigin = openUpward ? "bottom right" : "top right";

    const raf = requestAnimationFrame(() => {
      menuEl.style.opacity = "1";
      menuEl.style.transform = "translateY(0) scale(1)";
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenu?.id]);

  // Close the portal menu on outside click/scroll/resize, but let clicks on
  // the trigger button or inside the menu itself behave normally (the
  // trigger's own onClick already toggles it; menu item onClicks run their
  // action and close the menu themselves).
  useEffect(() => {
    if (!openMenu) return undefined;
    const handlePointerDown = (e) => {
      if (e.target.closest("[data-student-actions]")) return;
      closeMenu();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [openMenu]);

  const handleEdit = (record) => {
    navigate(`/estudantes/editar/${record.id}`, { state: { registration: record } });
  };

  const handleInactivate = async (record) => {
    if (!(await confirm(t("estudante.list.confirm.inactivate")))) return;
    setActionLoadingId(record.id);
    try {
      await inactivateRegistration(
        record.id,
        t("estudante.list.inactivate.default.reason"),
        getUserInfo()?.id,
      );
      toast.success(t("estudante.list.inactivate.success"));
      fetchRegistrations(filterYear);
    } catch (err) {
      toast.error(t("estudante.list.action.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRemove = async (record) => {
    if (!(await confirm(t("estudante.list.confirm.remove")))) return;
    setActionLoadingId(record.id);
    try {
      await removeStudent(record.studentId, {
        registrationId: record.id,
        payments: Array.isArray(record.payments)
          ? record.payments
          : Object.values(record.payments || {}),
        activatedBy: getUserInfo()?.id,
      });
      toast.success(t("estudante.list.remove.success"));
      fetchRegistrations(filterYear);
    } catch (err) {
      toast.error(t("estudante.list.action.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const openPdf = (resp) => {
    if (resp?.data?.pdf) {
      window.open(resp.data.pdf, "_blank", "noopener,noreferrer");
    } else {
      toast.error(t("estudante.list.print.error"));
    }
  };

  const handlePrintComprovativo = async (record) => {
    setActionLoadingId(record.id);
    try {
      const isRenewal = record.lastRegistrationYear === currentYear - 1;
      const resp = await printComprovativo({
        registrationId: record.id,
        studentId: record.studentId,
        isRenewal,
      });
      openPdf(resp);
    } catch (err) {
      toast.error(t("estudante.list.print.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePrintFicha = async (record) => {
    setActionLoadingId(record.id);
    try {
      const resp = await printFicha(record.id);
      openPdf(resp);
    } catch (err) {
      toast.error(t("estudante.list.print.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const openNotify = (records) => {
    setNotifyTargets(records);
  };

  const openNotifySelected = () => {
    const records = filtered.filter((r) => selectedIds.includes(r.id));
    if (records.length === 0) return;
    openNotify(records);
  };

  const openNotifyAll = () => {
    if (filtered.length === 0) return;
    openNotify(filtered);
  };

  const notifyPairs = useMemo(
    () =>
      notifyTargets.map((rec) => ({
        registrationId: rec.id,
        contact: rec.student?.carier?.contact,
      })),
    [notifyTargets],
  );

  const openExtraModal = (mode, record) => {
    setExtraModalMode(mode);
    setExtraModalTarget(record);
  };

  const handleExtraModalDone = () => {
    setExtraModalTarget(null);
    fetchRegistrations(filterYear);
  };

  const openAllocateModal = (record) => setAllocateTarget(record);

  const handleAllocateDone = () => {
    setAllocateTarget(null);
    fetchClassrooms(filterYear);
  };

  const handleNotificationSent = () => {
    setNotifyTargets([]);
    setSelectedIds([]);
  };

  return (
    <div className='row gy-4'>
      <style>{`
        .estudantes-stat-card {
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .estudantes-stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 32px -12px rgba(15, 23, 42, 0.16), 0 4px 10px -4px rgba(15, 23, 42, 0.08);
        }
        .estudantes-list-card {
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05), 0 1px 2px rgba(15, 23, 42, 0.03);
        }
        .estudantes-action-trigger {
          transition: background-color .15s ease, box-shadow .15s ease, border-color .15s ease;
        }
        .estudantes-action-trigger:hover:not(:disabled) {
          background-color: var(--primary-50);
          border-color: var(--primary-200);
          box-shadow: 0 4px 10px -4px rgba(15, 23, 42, 0.12);
        }
      `}</style>
      <ToastContainer />
      {ConfirmModal}

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card estudantes-stat-card flex-fill radius-12 border-0'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:users-group-rounded-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{filtered.length}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("estudante.list.stat.active")}
                </span>
              </div>
            </div>
          </div>
          <div className='card estudantes-stat-card flex-fill radius-12 border-0'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-info-focus text-info-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:gender-male' />
              </span>
              <div>
                <h6 className='mb-0'>{maleCount}</h6>
                <span className='text-secondary-light text-sm'>{t("global.male")}</span>
              </div>
            </div>
          </div>
          <div className='card estudantes-stat-card flex-fill radius-12 border-0'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-warning-focus text-warning-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:gender-female' />
              </span>
              <div>
                <h6 className='mb-0'>{femaleCount}</h6>
                <span className='text-secondary-light text-sm'>{t("global.female")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card estudantes-list-card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("estudante.list.title")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("estudante.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/estudantes/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("estudante.list.add")}
              </button>
            </div>

            <div className='d-flex flex-wrap align-items-center gap-12 p-12 radius-8 bg-primary-50 mb-16'>
              {selectedIds.length > 0 && (
                <>
                  <span className='text-sm fw-medium text-primary-600'>
                    {t("estudante.list.selected.count", { count: selectedIds.length })}
                  </span>
                  <button
                    type='button'
                    className='btn btn-primary-600 btn-sm d-flex align-items-center gap-2'
                    data-bs-toggle='modal'
                    data-bs-target='#notifyStudentModal'
                    onClick={openNotifySelected}
                  >
                    <Icon icon='mage:message-notify' /> {t("estudante.list.action.notify")}
                  </button>
                  <button
                    type='button'
                    className='btn btn-link btn-sm text-secondary-light'
                    onClick={() => setSelectedIds([])}
                  >
                    {t("estudante.list.selected.clear")}
                  </button>
                </>
              )}
              <button
                type='button'
                className='btn border border-primary-600 text-primary-600 btn-sm d-flex align-items-center gap-2 ms-auto'
                data-bs-toggle='modal'
                data-bs-target='#notifyStudentModal'
                onClick={openNotifyAll}
              >
                <Icon icon='mage:message-notify' /> {t("estudante.list.action.notifyAll")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("estudante.field.selectedYear")}</label>
                  <select
                    className='form-control form-select'
                    value={filterYear}
                    onChange={(e) => {
                      setFilterYear(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("global.name")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("estudante.list.search.placeholder")}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("global.gender")}</label>
                  <select
                    className='form-control form-select'
                    value={sex}
                    onChange={(e) => {
                      setSex(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("estudante.list.filter.all")}</option>
                    <option value='M'>{t("global.male")}</option>
                    <option value='F'>{t("global.female")}</option>
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("estudante.field.turma"))}</label>
                  <select
                    className='form-control form-select'
                    value={turma}
                    onChange={(e) => {
                      setTurma(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("estudante.list.filter.all")}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("estudante.field.classroom")}</label>
                  <select
                    className='form-control form-select'
                    value={classroomId}
                    onChange={(e) => {
                      setClassroomId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("estudante.list.filter.all")}</option>
                    {classrooms.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.className}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }}>
                      <div className='form-check style-check d-flex align-items-center'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={pageItems.length > 0 && pageItems.every((r) => selectedIds.includes(r.id))}
                          onChange={toggleSelectAllOnPage}
                        />
                      </div>
                    </th>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>
                      {t("global.name")}
                    </th>
                    <th scope='col'>
                      {t("global.contact")}
                    </th>
                    <th scope='col'>
                      {t("global.gender")}
                    </th>
                    <th scope='col'>
                      {t(levelKey("estudante.field.turma"))}
                    </th>
                    <th scope='col'>
                      {t("global.birth.date")}
                    </th>
                    <th scope='col'>
                      {t("estudante.field.selectedYear")}
                    </th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <TableLoadingRow colSpan={9} label={t("estudante.list.loading")} />
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={9} className='text-center text-secondary-light py-32'>
                        {t("estudante.list.empty")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((record) => {
                    const isExpanded = expandedId === record.id;
                    const freq = frequencyFor(record);
                    const busy = actionLoadingId === record.id;
                    return (
                      <React.Fragment key={record.id}>
                        <tr
                          role='button'
                          onClick={() => toggleExpand(record.id)}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className='form-check style-check d-flex align-items-center'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={selectedIds.includes(record.id)}
                                onChange={() => toggleSelect(record.id)}
                              />
                            </div>
                          </td>
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{record.student?.name}</td>
                          <td>{record.student?.carier?.contact || record.student?.mobile || "-"}</td>
                          <td>{record.student?.sex === "M" ? t("global.male") : t("global.female")}</td>
                          <td>{freq?.description || "-"}</td>
                          <td>{formatDate(record.student?.birthDate)}</td>
                          <td>{record.year || filterYear}</td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <button
                              type='button'
                              data-student-actions
                              className='estudantes-action-trigger w-32-px h-32-px d-inline-flex align-items-center justify-content-center radius-8 border bg-base'
                              aria-expanded={openMenu?.id === record.id}
                              disabled={busy}
                              onClick={(e) => toggleMenu(e, record)}
                            >
                              <Icon icon={busy ? "svg-spinners:ring-resize" : "entypo:dots-three-vertical"} />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <div className='row gy-3'>
                                  <div className='col-md-3 text-center'>
                                    <div className='w-160-px h-160-px radius-12 border overflow-hidden bg-neutral-100 d-flex align-items-center justify-content-center mx-auto shadow-sm'>
                                      {record.student?.imageUrl ? (
                                        <img
                                          src={record.student.imageUrl}
                                          alt={record.student?.name}
                                          className='w-100 h-100 object-fit-cover'
                                        />
                                      ) : (
                                        <Icon
                                          icon='solar:user-rounded-bold'
                                          className='text-3xl text-neutral-400'
                                        />
                                      )}
                                    </div>
                                    <div className='mt-16 d-flex flex-column align-items-center gap-8'>
                                      <span className='px-12 py-4 radius-8 fw-medium text-sm bg-success-focus text-success-main'>
                                        {t("global.active").toUpperCase()}
                                      </span>
                                      {freq && (
                                        <span className='px-12 py-4 radius-8 fw-medium text-sm bg-primary-50 text-primary-600 d-flex align-items-center gap-1'>
                                          <Icon icon='solar:square-academic-cap-outline' />
                                          {freq.description}
                                        </span>
                                      )}
                                      <span className='px-12 py-4 radius-8 fw-medium text-sm bg-lilac-50 text-lilac-600 d-flex align-items-center gap-1'>
                                        <Icon icon='solar:calendar-outline' />
                                        {record.year || filterYear}
                                      </span>
                                    </div>
                                  </div>
                                  <div className='col-md-9'>
                                    <ul
                                      className='nav nav-pills mb-16 gap-2'
                                      id={`estudante-tabs-${record.id}`}
                                      role='tablist'
                                    >
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link active radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#personal-${record.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("estudante.expand.tab.personal")}
                                        </button>
                                      </li>
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#family-${record.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("estudante.expand.tab.family")}
                                        </button>
                                      </li>
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#enrollment-${record.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("estudante.expand.tab.enrollment")}
                                        </button>
                                      </li>
                                    </ul>
                                    <div className='tab-content'>
                                      <div
                                        className='tab-pane fade show active'
                                        id={`personal-${record.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("estudante.field.name")}
                                              </th>
                                              <td colSpan={3}>
                                                <strong>{record.student?.name}</strong>
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.docType")}
                                              </th>
                                              <td>
                                                {record.student?.docType} {record.student?.docNumber}
                                              </td>
                                              <th className='bg-neutral-50'>{t("global.gender")}</th>
                                              <td>
                                                {record.student?.sex === "M"
                                                  ? t("global.male")
                                                  : t("global.female")}
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("global.birth.date")}
                                              </th>
                                              <td>{formatDate(record.student?.birthDate)}</td>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.contact")}
                                              </th>
                                              <td>
                                                {record.student?.carier?.contact ||
                                                  record.student?.mobile ||
                                                  "-"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.studentAddress")}
                                              </th>
                                              <td colSpan={3}>{record.student?.address || "-"}</td>
                                            </tr>
                                            {record.student?.sufferFromAnyIllness && (
                                              <tr>
                                                <th className='bg-neutral-50'>
                                                  {t("estudante.field.deseaseDesc")}
                                                </th>
                                                <td colSpan={3}>
                                                  {record.student.sufferFromAnyIllness}
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                      <div
                                        className='tab-pane fade'
                                        id={`family-${record.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("estudante.field.fatherName")}
                                              </th>
                                              <td colSpan={3}>
                                                {record.student?.fatherName || "-"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.motherName")}
                                              </th>
                                              <td colSpan={3}>
                                                {record.student?.motherName || "-"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.carierName")}
                                              </th>
                                              <td>{record.student?.carier?.name || "-"}</td>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.kinshipDegree")}
                                              </th>
                                              <td>{record.student?.carier?.kinshipDegree || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.carierContact")}
                                              </th>
                                              <td colSpan={3}>
                                                {record.student?.carier?.contact || "-"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.field.secondCarierContact")}
                                              </th>
                                              <td colSpan={3}>
                                                {record.student?.carier?.secondContact || "-"}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      <div
                                        className='tab-pane fade'
                                        id={`enrollment-${record.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("estudante.expand.turma")}
                                              </th>
                                              <td>{freq?.description || "-"}</td>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.expand.year")}
                                              </th>
                                              <td>{record.year || filterYear}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.expand.paymentMethod")}
                                              </th>
                                              <td>{record.paymentMethod || "-"}</td>
                                              <th className='bg-neutral-50'>
                                                {t("estudante.summary.monthlyPayment")}
                                              </th>
                                              <td>
                                                <strong className='text-success-main'>
                                                  {record.monthlyPayment ?? 0}
                                                </strong>
                                              </td>
                                            </tr>
                                            {hasSpecialHourSchedule() && (
                                              <tr>
                                                <th className='bg-neutral-50'>
                                                  {t("estudante.expand.scheduleType")}
                                                </th>
                                                <td colSpan={3}>
                                                  {record.scheduleType === "7h-15h"
                                                    ? t("estudante.field.scheduleType.special")
                                                    : t("estudante.field.scheduleType.standard")}
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("estudante.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <NotifyModal
        modalId='notifyStudentModal'
        pairs={notifyPairs}
        count={notifyTargets.length}
        onSent={handleNotificationSent}
      />

      <StudentExtraModal
        modalId='studentExtraModal'
        mode={extraModalMode}
        record={extraModalTarget}
        onDone={handleExtraModalDone}
      />

      <AllocateClassroomModal
        modalId='allocateClassroomModal'
        record={allocateTarget}
        frequencies={frequencies}
        classrooms={classrooms}
        onDone={handleAllocateDone}
      />

      {openMenu &&
        createPortal(
          <ul
            ref={menuRef}
            data-student-actions
            className='p-8 border bg-base radius-12 mb-0'
            style={{
              position: "fixed",
              top: openMenu.triggerBottom + MENU_GAP,
              left: openMenu.left,
              width: MENU_WIDTH,
              zIndex: 1080,
              listStyle: "none",
              boxShadow: "0 12px 32px -8px rgba(15, 23, 42, 0.18), 0 4px 10px -4px rgba(15, 23, 42, 0.1)",
              opacity: 0,
              transform: "translateY(-6px) scale(0.98)",
              transition: "opacity .14s ease, transform .14s ease",
            }}
          >
            <ActionMenuItem
              icon='solar:pen-2-linear'
              tone='primary'
              label={t("global.edit")}
              onClick={() => {
                closeMenu();
                handleEdit(openMenu.record);
              }}
            />
            <ActionMenuItem
              icon='ic:outline-block'
              tone='warning'
              label={t("estudante.list.action.inactivate")}
              onClick={() => {
                closeMenu();
                handleInactivate(openMenu.record);
              }}
            />
            <ActionMenuItem
              icon='mingcute:delete-2-line'
              tone='danger'
              label={t("estudante.list.action.remove")}
              onClick={() => {
                closeMenu();
                handleRemove(openMenu.record);
              }}
            />
            <li className='my-6 border-top' />
            <ActionMenuItem
              icon='mdi:google-classroom'
              tone='primary'
              label={t("estudante.list.action.allocateTurma")}
              modalTarget='#allocateClassroomModal'
              onClick={() => {
                openAllocateModal(openMenu.record);
                closeMenu();
              }}
            />
            <li className='my-6 border-top' />
            <ActionMenuItem
              icon='solar:printer-linear'
              tone='info'
              label={t("estudante.list.action.print.comprovativo")}
              onClick={() => {
                closeMenu();
                handlePrintComprovativo(openMenu.record);
              }}
            />
            <ActionMenuItem
              icon='solar:printer-linear'
              tone='info'
              label={t("estudante.list.action.print.ficha")}
              onClick={() => {
                closeMenu();
                handlePrintFicha(openMenu.record);
              }}
            />
            <li className='my-6 border-top' />
            <ActionMenuItem
              icon='solar:add-circle-linear'
              tone='success'
              label={t("estudante.list.action.addExtra")}
              modalTarget='#studentExtraModal'
              onClick={() => {
                openExtraModal("add", openMenu.record);
                closeMenu();
              }}
            />
            <ActionMenuItem
              icon='solar:minus-circle-linear'
              tone='danger'
              label={t("estudante.list.action.removeExtra")}
              modalTarget='#studentExtraModal'
              onClick={() => {
                openExtraModal("remove", openMenu.record);
                closeMenu();
              }}
            />
            <li className='my-6 border-top' />
            <ActionMenuItem
              icon='mage:message-notify'
              tone='lilac'
              label={t("estudante.list.action.notify")}
              modalTarget='#notifyStudentModal'
              onClick={() => {
                openNotify([openMenu.record]);
                closeMenu();
              }}
            />
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default ListaEstudantesLayer;
